// use-macro-history — täglicher Snapshot + Trend-Block für EVE-Prompts.
// V1-Pendant: src/hooks/useMacroHistory.js (saveDailySnapshot, getMacroTrendContext).
//
// Auto-Save-Strategie: bei jeder fullData-Aktualisierung wird probiert, einen
// täglichen Snapshot zu speichern (idempotent — der Store skippt wenn der Tag schon
// existiert).

import { useCallback, useEffect, useMemo } from 'react';
import { formatMacroTrendContext } from '@/lib/macro-history';
import {
  selectMacroEntries,
  useMacroHistoryStore,
} from '@/stores/macro-history-store';
import { selectFullData, useMarketStore } from '@/stores/market-store';
import { deriveMarketStats } from '@/lib/market-cache';
import type { MacroSnapshotEntry } from '@/types/macro-history';

export interface UseMacroHistoryReturn {
  entries: MacroSnapshotEntry[];
  /** Lesbarer Trend-Block für EVE-Prompt. `null` wenn <2 Snapshots. */
  trendContext: string | null;
  /** Manuell speichern (Setter, z.B. nach Regime-Erkennung). */
  saveSnapshot: () => MacroSnapshotEntry | null;
  clearAll: () => void;
}

export function useMacroHistory(): UseMacroHistoryReturn {
  const entries = useMacroHistoryStore(selectMacroEntries);
  const saveDaily = useMacroHistoryStore((s) => s.saveDailySnapshot);
  const clearAll = useMacroHistoryStore((s) => s.clearAll);
  const fullData = useMarketStore(selectFullData);

  const trendContext = useMemo(() => formatMacroTrendContext(entries), [entries]);

  const saveSnapshot = useCallback((): MacroSnapshotEntry | null => {
    if (!fullData) return null;
    const stats = deriveMarketStats(fullData);
    if (!stats) return null;
    return saveDaily(fullData, stats);
  }, [fullData, saveDaily]);

  // Auto-Save bei jedem neuen fullData (idempotent für den aktuellen Tag).
  useEffect(() => {
    if (!fullData) return;
    saveSnapshot();
  }, [fullData, saveSnapshot]);

  return { entries, trendContext, saveSnapshot, clearAll };
}
